import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Star,
  Quote,
  Award,
  Shield,
  ThumbsUp,
  MessageCircle,
} from 'lucide-react';
import GlassCard from '../components/GlassCard';
import { reviews } from '../data/reviews';

const ReviewsPage = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';
  const [filter, setFilter] = useState('all');

  const content = {
    title: isAr ? 'آراء العملاء' : 'Customer Reviews',
    subtitle: isAr
      ? 'اقرأ ما يقوله عملاؤنا عن تجربتهم مع سوبر كلاسيكي'
      : 'Read what our customers say about their Super Classy experience',
    stats: [
      { label: isAr ? 'إجمالي المراجعات' : 'Total Reviews', key: 'total' },
      { label: isAr ? 'متوسط التقييم' : 'Average Rating', key: 'average' },
      { label: isAr ? 'تقييم ٥ نجوم' : '5-Star Reviews', key: 'fiveStar' },
      { label: isAr ? 'معدل الرد' : 'Response Rate', key: 'responseRate' },
    ],
    filters: {
      all: isAr ? 'الكل' : 'All Reviews',
      stars: isAr ? 'نجوم' : 'Stars',
      star: isAr ? 'نجمة' : 'Star',
    },
    trust: [
      {
        icon: <Shield className='h-8 w-8 text-green-500' />,
        title: isAr ? 'مراجعات موثقة' : 'Verified Reviews',
        desc: isAr
          ? 'جميع المراجعات من عملاء حقيقيين موثقين'
          : 'All reviews are from verified customers',
        bg: 'bg-green-500/10',
      },
      {
        icon: <ThumbsUp className='h-8 w-8 text-blue-500' />,
        title: isAr ? 'تجارب واقعية' : 'Real Experiences',
        desc: isAr
          ? 'تعليقات حقيقية من عملاء حقيقيين'
          : 'Authentic feedback from real customers',
        bg: 'bg-blue-500/10',
      },
      {
        icon: <Award className='h-8 w-8 text-purple-500' />,
        title: isAr ? 'خدمة مميزة' : 'Award Winning',
        desc: isAr
          ? 'حاصلون على جائزة أفضل خدمة ٢٠٢٣'
          : 'Best Service Award 2023',
        bg: 'bg-purple-500/10',
      },
    ],
    cta: {
      title: isAr ? 'شاركنا تجربتك' : 'Share Your Experience',
      desc: isAr
        ? 'هل نالت خدمتنا إعجابك؟ شارك برأيك وساعد الآخرين في اكتشاف أفضل عناية بالسيارات في الكويت.'
        : 'Loved our service? Share your review and help others discover premium car care in Kuwait.',
      whatsapp: isAr ? 'شارك عبر واتساب' : 'Share on WhatsApp',
    },
  };

  const filteredReviews =
    filter === 'all'
      ? reviews
      : reviews.filter((review) => review.rating === parseInt(filter));

  const stats = {
    total: reviews.length,
    average: (
      reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
    ).toFixed(1),
    fiveStar: reviews.filter((r) => r.rating === 5).length,
    responseRate: isAr ? '٪٩٨' : '98%',
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, index) => (
      <Star
        key={index}
        className={`h-5 w-5 ${
          index < rating
            ? 'fill-yellow-400 text-yellow-400'
            : 'fill-gray-300 dark:fill-gray-700 text-gray-300 dark:text-gray-700'
        }`}
      />
    ));
  };

  return (
    <div className='pt-32' dir={isAr ? 'rtl' : 'ltr'}>
      <div className='section-padding py-20'>
        <div className='max-w-7xl mx-auto'>
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className='text-center mb-12'
          >
            <h1 className='text-5xl md:text-7xl font-black mb-6 text-gradient'>
              {content.title}
            </h1>
            <p className='text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed'>
              {content.subtitle}
            </p>
          </motion.div>

          {/* Stats Bar */}
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-12'>
            {content.stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <GlassCard
                  className='p-6 text-center shadow-lg border border-white/10'
                  lang={lang}
                >
                  <div className='inline-flex p-3 premium-gradient rounded-xl mb-3 shadow-md'>
                    {index === 0 ? (
                      <Quote className='h-6 w-6 text-white' />
                    ) : index === 1 ? (
                      <Star className='h-6 w-6 text-white' />
                    ) : index === 2 ? (
                      <Award className='h-6 w-6 text-white' />
                    ) : (
                      <MessageCircle className='h-6 w-6 text-white' />
                    )}
                  </div>
                  <div className='text-3xl font-black mb-1'>
                    {index === 1
                      ? `${stats.average}/5`
                      : index === 3
                        ? stats.responseRate
                        : stats[stat.key]}
                  </div>
                  <div className='text-xs font-bold text-gray-400 uppercase tracking-widest'>
                    {stat.label}
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>

          {/* Filter Bar */}
          <div className='flex flex-wrap gap-3 mb-12 justify-center'>
            {['all', '5', '4', '3'].map((rating) => (
              <button
                key={rating}
                onClick={() => setFilter(rating)}
                className={`px-6 py-2.5 rounded-full font-bold text-sm transition-all duration-300 ${
                  filter === rating
                    ? 'premium-gradient text-white shadow-lg scale-105'
                    : 'glass-card hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                {rating === 'all'
                  ? content.filters.all
                  : `${rating} ${rating === '5' ? content.filters.stars : content.filters.star}`}
              </button>
            ))}
          </div>

          {/* Reviews Grid */}
          <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16'>
            {filteredReviews.map((review, index) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <GlassCard
                  className='p-8 h-full flex flex-col border border-white/10 shadow-xl'
                  lang={lang}
                >
                  <div className='flex items-center gap-1 mb-6'>
                    {renderStars(review.rating)}
                  </div>
                  <p className='text-gray-700 dark:text-gray-300 mb-8 italic flex-grow leading-relaxed font-medium'>
                    "
                    {isAr ? review.commentAr || review.comment : review.comment}
                    "
                  </p>
                  <div
                    className={`flex items-center justify-between pt-6 border-t border-gray-100 dark:border-gray-800 ${isAr ? 'flex-row-reverse' : ''}`}
                  >
                    <div className={isAr ? 'text-right' : 'text-left'}>
                      <div className='font-black text-lg'>
                        {isAr ? review.nameAr || review.name : review.name}
                      </div>
                      <div className='text-sm font-bold text-royal-purple-500'>
                        {isAr ? review.carAr || review.car : review.car}
                      </div>
                    </div>
                    <div className='text-xs font-bold text-gray-400'>
                      {review.date}
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className='mb-20'
          >
            <GlassCard
              className='p-10 border border-white/10 shadow-2xl bg-royal-purple-500/5'
              lang={lang}
            >
              <div className='grid md:grid-cols-3 gap-12'>
                {content.trust.map((item, i) => (
                  <div key={i} className='text-center group'>
                    <div
                      className={`inline-flex p-4 ${item.bg} rounded-2xl mb-6 transform group-hover:rotate-12 transition-transform shadow-sm`}
                    >
                      {item.icon}
                    </div>
                    <h3 className='text-xl font-black mb-3'>{item.title}</h3>
                    <p className='text-gray-500 font-medium text-sm leading-relaxed'>
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </GlassCard>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className='text-center'
          >
            <GlassCard
              className='p-10 inline-block border border-white/20 shadow-2xl'
              lang={lang}
            >
              <h2 className='text-3xl font-black mb-4 text-gradient'>
                {content.cta.title}
              </h2>
              <p className='text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto font-medium leading-relaxed'>
                {content.cta.desc}
              </p>
              <div className='flex justify-center'>
                <button
                  onClick={() =>
                    window.open('https://wa.me/96550040026', '_blank')
                  }
                  className='inline-flex items-center gap-3 px-10 py-4 bg-green-500 hover:bg-green-600 text-white rounded-full font-black text-lg shadow-xl hover:shadow-green-500/30 transition-all active:scale-95'
                >
                  <MessageCircle className='h-6 w-6' />
                  {content.cta.whatsapp}
                </button>
              </div>
            </GlassCard>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ReviewsPage;
